import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { WholeNumberCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/wholeNumberCustomFieldDefinition";
import type { WholeNumberCustomFieldValue } from "@btnet/services-btnet/handlers/models/wholeNumberCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTInputNumber } from "commonComponents/btWrappers/BTInputNumber/BTInputNumber";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

export const WholeNumberCustomFieldHandler: ICustomFieldTypeHandler<
    number,
    WholeNumberCustomFieldValue,
    WholeNumberCustomFieldDefinition
> = {
    getValue(field): number | null {
        return field.value ?? null;
    },

    setValue(field, value): WholeNumberCustomFieldValue {
        return { ...field, value: value ?? 0 };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: number | null,
    ): WholeNumberCustomFieldValue {
        return {
            dataType: "WholeNumber",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? 0,
        };
    },

    compare(valueA: number | null, valueB: number | null): number {
        const a = valueA ?? 0;
        const b = valueB ?? 0;
        return a - b;
    },

    renderDisplay(value: number | null): ReactNode {
        const displayValue =
            value !== null && value !== undefined ? value.toString() : friendlyDefaultDisplayValue;
        return <BTText size="normal-md" text={displayValue} />;
    },

    renderEditor(
        value: number | null,
        customFieldId: number,
        onChange: (newValue: number | null) => void,
        onBlur: () => void,
    ): ReactNode {
        return (
            <BTInputNumber
                id={`wholeNumberCustomFieldEditor-${customFieldId}`}
                data-testid="wholeNumberCustomFieldEditor"
                value={value}
                step={1}
                min={-999999999999}
                max={999999999999}
                onChange={(_field, val) => onChange(val ?? null)}
                onBlur={() => onBlur()}
                roundToDecimalPlaces={0}
                allowNull
                formMode="click-to-edit"
            />
        );
    },

    getAgGridCellEditor() {
        return undefined;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
